/**
 * VistoeraTASK-00556 — network fence for the READ_ONLY Canvas (layer 5, see
 * canvasReadOnly.ts). Kept separate so the pure guard primitives stay free of the
 * axios/api module (node-testable).
 */

import { saasApi } from '../../../api'
import { CANVAS_READ_ONLY_DENIED_CODE, notifyCanvasReadOnlyDenied } from './canvasReadOnly'

// ---------------------------------------------------------------------------
// Network fence
// ---------------------------------------------------------------------------

interface FenceState {
  workspaceId: string
  projectId: string
  /** Localised read-only copy supplied by the Canvas (i18n). */
  message: string
}

let fence: FenceState | null = null
let fenceInstalled = false

/** POST endpoints that only READ (capability / pricing lookups) or that are an
 * explicitly permitted action of a read-only viewer (copy the project). */
function isAllowedFencedRequest(method: string, url: string, state: FenceState): boolean {
  const path = url.split('?')[0]
  if (/\/auth\//.test(path)) return true
  if (/eligible-models$/.test(path)) return true
  if (/resolve-check$/.test(path)) return true
  if (/\/(quote|estimate|estimates|preview-cost)$/.test(path)) return true
  if (
    method === 'post' &&
    path.endsWith(`/content-production/${encodeURIComponent(state.projectId)}/duplicate`)
  ) {
    return true
  }
  return false
}

function ensureFenceInstalled(): void {
  if (fenceInstalled) return
  fenceInstalled = true
  saasApi.interceptors.request.use((config) => {
    if (!fence) return config
    const method = (config.method || 'get').toLowerCase()
    if (method === 'get' || method === 'head' || method === 'options') return config
    const url = String(config.url || '')
    if (isAllowedFencedRequest(method, url, fence)) return config
    notifyCanvasReadOnlyDenied()
    const blocked = (window as unknown as { __pcvReadOnlyBlocked?: string[] })
    blocked.__pcvReadOnlyBlocked = [...(blocked.__pcvReadOnlyBlocked ?? []), `${method.toUpperCase()} ${url}`]
    // Fail through the adapter (not a rejected interceptor) so the shared
    // response interceptor still maps `detail.{code,message}` onto the error
    // instead of rewriting it into a "no connection" transport message.
    const message = fence.message
    config.adapter = () =>
      Promise.reject(
        Object.assign(new Error(message), {
          isAxiosError: true,
          config,
          response: {
            status: 403,
            statusText: 'Forbidden',
            headers: {},
            config,
            data: { detail: { code: CANVAS_READ_ONLY_DENIED_CODE, message } },
          },
        }),
      )
    return config
  })
}

/** Activate (state) / deactivate (null) the fence for the mounted Canvas. */
export function setCanvasReadOnlyNetworkFence(state: FenceState | null): void {
  ensureFenceInstalled()
  fence = state
}
